import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, createCommentVNode as _createCommentVNode, vShow as _vShow, withCtx as _withCtx, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-bee2836c"),n=n(),_popScopeId(),n)
const _hoisted_1 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", {
  class: "drag-handle",
  id: "dragHandle"
}, [
  /*#__PURE__*/_createElementVNode("i", { class: "fa-solid fa-grip-vertical" })
], -1))
const _hoisted_2 = {
  key: 0,
  class: "selected-image-options"
}
const _hoisted_3 = { class: "button-container" }
const _hoisted_4 = {
  key: 0,
  class: "button-group"
}
const _hoisted_5 = {
  key: 1,
  class: "button-group"
}
const _hoisted_6 = { class: "file-uploader" }

import { displayQuestionAlert } from "@/alerts/questionAlert";
import CarouselPicture from "@/components/carousel-pictures/carousel-picture.vue";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import { PictureIds } from "@/types/picture-management/picture-ids";
import displayErrorMessage from "@/utils/ErrorHandling";
import { PropertyImageType } from "acquaint-api/constants/PropertyImageType";
import { computed, inject, ref, nextTick, watch, onMounted } from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import FileUpload from "@/components/ui/inputs/file-upload.vue";
import DxCheckBox from "devextreme-vue/check-box";
import { Properties } from "acquaint-api/models/Properties";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { standardInfoAlert } from "@/alerts/standardInfoAlert";
import { CarouselPictureType } from "@/enums/CarouselRecordType";
import { DxButton, DxContextMenu } from "devextreme-vue";
import ScrollView from "@/components/ui/scroll-view.vue";
import ImagerViewerModal from "@/components/carousel-pictures/image-viewer-modal.vue";
import AiImageEnhancerModal from "@/components/ai/ai-image-enhancer-modal.vue";
import { displayCancelConfirmationWarningAlert } from "@/alerts/cancelConfirmationWarningAlert";
import { ContextMenuItem } from "@/types/ui/menus/ContextMenuItem";
import { sizes } from "@/utils/media-query";


export default /*#__PURE__*/_defineComponent({
  __name: 'property-listing-picture-management',
  props: {
  /**
   * This refreshes the data.
   * @type {number}
   * @required
   */
  refreshData: {
    type: Number,
    required: true,
  },
  /**
   * The unique identifier for the record the pictures belong to.
   * @type {string}
   * @required
   */
  recordId: {
    type: Number,
    required: true,
  },
  /**
   * The count of the image to be displayed.
   * @type {number}
   * @required
   */
  pictureCount: {
    type: Number,
    required: true,
  },
},
  emits: [
  /**
   * Emitted when the image list is updated, either by adding or deleting.
   */
  "image-list-updated",
  /**
   * Emitted when the image list is updated, and one of the images cannot be displayed.
   */
  "invalidImages",
],
  setup(__props, { emit }) {

const props = __props;



const numberPictures = ref();
const imageList = ref<PictureIds[]>([]);
const disableButtons = ref(false);
const propertyAllData = ref<Properties>();
const resetFiles = ref(false);
const safeMode = ref(false);
const pictureLoadingErrorDisplayed = ref(false);
const apiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const triggerFileUploading = ref(false);
const isWebMainPicture = (imageId: number) =>
  imageId === propertyAllData.value?.pictureWebMain;
const rearrangeDisabled = computed(
  () => safeMode.value || disableButtons.value,
);
const photoContainerClasses = computed(() =>
  disableButtons.value
    ? ["photo-container", "processing"]
    : ["photo-container"],
);
const autoScrollToBottom = ref(false);
const selectedItem = ref(-1);
const selectedItemAsImageId = computed(
  () => selectedItem.value + 1,
);
const itemSelected = ref(false);
const uploadFailed = ref(false);
const displayZoomModal = ref(false);
const displayAIImageModal = ref(false);
const invalidIndexes = ref<number[]>([]);
const showFunctionsMenu = ref(false);
const contextMenuId = `image-actions-menu-${props.recordId}`;
const menuItems = computed((): ContextMenuItem[] => [
  {
    text: "Add Photo",
    key: "add",
    icon: "fa-solid fa-plus",
  },
  {
    text: "Delete Photo",
    key: "delete",
    icon: "fa-solid fa-trash",
    disabled: selectedItem.value === -1 || disableButtons.value,
  },
  {
    text: "Zoom",
    key: "zoom",
    icon: "fa-solid fa-search-plus",
    disabled: selectedItem.value === -1,
  },
]);

const onMenuItemClick = (e: any) => {
  switch (e.itemData.key) {
    case "add":
      onAddClick();
      break;
    case "delete":
      onDeleteClick();
      break;
    case "zoom":
      onDisplayZoomModal();
      break;
  }
};



/**
 * This prevents the same images from being rerendered when the record id is updated.
 * We use this for when a user is navigating between properties and using the AI property listing wizard as otherwise,
 * a bug is occurs where the inital property has 10 prictures for example and the second has 1 image. When using the wizard
 * for the second image, 9 erroring images are displayed.
 * @type {boolean}
 * @default
 */
watch(
  () => props.recordId,
  () => {
    numberPictures.value = 0;
    imageList.value = [];
    resetProcessingFields();
  },
);

watch(
  () => props.refreshData,
  async () => {
    itemSelected.value = false;

    await fetchAllPropertyData();
    // When modal is shown, populate the image list so the VueDraggable component can track the images
    populateImageList();
  },
);

onMounted(async () => {
  itemSelected.value = false;
  await fetchAllPropertyData();

  // When modal is shown, populate the image list so the VueDraggable component can track the images
  populateImageList();
});

const fetchAllPropertyData = async () => {
  try {
    propertyAllData.value = await apiClient.httpGet(
      `database/properties/${props.recordId}`,
    );
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
  }
};

/** Pre-populates the image list array for the VueDraggable component to bind to */
const populateImageList = () => {
  if (imageList.value.length > 0) {
    return;
  }

  numberPictures.value = props.pictureCount;
  imageList.value = [];
  pictureLoadingErrorDisplayed.value = false;
  safeMode.value = false;

  if (!propertyAllData.value) {
    return;
  }

  for (let i = 0; i < numberPictures.value; i++) {
    imageList.value.push({ id: i + 1, key: generateImageKey(i + 1) });
  }
};

/** Generates a semi-random image key. This is used to give the VueDraggable component a unique key for each image
 *  This avoids incorrect images being removed from the screen when deleting images
 * @param id id of the image to generate a key for */
const generateImageKey = (id: number) =>
  id.toString() + Math.random().toString().replace("0.", "");

const resetProcessingFields = () => {
  itemSelected.value = false;
  selectedItem.value = -1;
  disableButtons.value = false;
};

const onErrorLoadingPicture = (index: number) => {
  safeMode.value = true;

  // To avoid annoying the user, only display the safe mode prompt once
  if (!pictureLoadingErrorDisplayed.value) {
    standardInfoAlert(
      "One or more pictures cannot be found. Please ensure all selected pictures are valid.",
    );
    pictureLoadingErrorDisplayed.value = true;
  }
  invalidIndexes.value.push(index);
  emit("invalidImages", invalidIndexes.value.length);
};

const onImageLoaded = (imageId: number) => {
  /* To avoid constant auto scrolling, which could annoy users if they manually scroll after adding a picture
   * After the last image has been added, reset the auto scroll indicator.
   * This needs to be done on a timeout, to allow the browser time to display the image */
  if (autoScrollToBottom.value === true && imageId === imageList.value.length) {
    setTimeout(() => (autoScrollToBottom.value = false), 600);
  }
};

// Logic for moving and selecting images
const movePicturePlace = async (e: any) => {
  disableButtons.value = true;

  let success = false;
  try {
    await apiClient.httpPostVoid(
      `app/properties/${props.recordId}/pictures/move`,
      {
        fromPictureNumber: e.moved.oldIndex + 1,
        toPictureNumber: e.moved.newIndex + 1,
      },
    );
    success = true;
  } catch (err) {
    success = false;
    displayErrorMessage("We could not move the position of this picture.");
  }

  resetProcessingFields();
  return success;
};

const selectImage = (index: number) => {
  if (disableButtons.value) {
    return;
  }

  if (selectedItem.value == index) {
    itemSelected.value = false;
    selectedItem.value = -1;
  } else {
    itemSelected.value = true;
    selectedItem.value = index;
  }
};

// Logic for deleting images
const onDeleteClick = async () => {
  await deletePhoto(selectedItemAsImageId.value);
};

const deletePhoto = async (pictureNumber: number) => {
  let success = true;

  const result = await displayQuestionAlert(
    "Are you sure you want to delete this picture?",
  );

  if (result.isDenied) {
    return false;
  }

  // If a user tries to delete an image, they shouldn't be able to interact with the buttons while the api call is being amde
  disableButtons.value = true;

  try {
    await apiClient.httpDeleteVoid(
      `app/properties/${props.recordId}/pictures/${pictureNumber}`,
    );
  } catch (err) {
    success = false;
    displayErrorMessage("We could not delete this picture.");
  }

  // If the api call has failed, the users should be able to interact with the buttons again
  if (!success) {
    disableButtons.value = false;
    return false;
  }

  // Instead of refreshing the whole screen, just remove the picture that was successfully deleted
  imageList.value.splice(pictureNumber - 1, 1);
  numberPictures.value = numberPictures.value - 1;

  // As all pictures will now have a new id, loop through pictures array and reset the id
  for (let i = 0; i < imageList.value.length; i++) {
    imageList.value[i].id = i + 1;
  }

  if (invalidIndexes.value.includes(selectedItem.value)) {
    // Once the invalid image has been deleted, we want to shift the indexes of all the invalid images downards.
    // This is because if a second invalid image is selected, it's index be 1 less than its initial position becasue an image has already been deleted
    invalidIndexes.value = invalidIndexes.value
      .filter((item) => {
        return item != selectedItem.value;
      })
      .map((item) => {
        return item - 1;
      });
    emit("invalidImages", invalidIndexes.value.length);
  }

  resetProcessingFields();
  emit("image-list-updated", numberPictures.value);
  return true;
};

const onDisplayZoomModal = () => {
  displayZoomModal.value = true;
};

// This opens the file explorer so users can select and upload images
const onAddClick = () => {
  disableButtons.value = true;
  triggerFileUploading.value = true;
};

const uploadAddedImage = async (e: File[]) => {
  if (!e) {
    resetProcessingFields();
    return;
  }

  uploadFailed.value = false;
  triggerFileUploading.value = false;
  resetFiles.value = true;

  for (let i = 0; i < e.length; i++) {
    if (!(await submitImageToAPI(e[i]))) {
      resetProcessingFields();
      resetFiles.value = false;
      continue;
    }

    numberPictures.value++;

    await nextTick();

    imageList.value.push({
      id: numberPictures.value,
      key: generateImageKey(numberPictures.value),
    });
  }
  resetFiles.value = false;
  resetProcessingFields();
  autoScrollToBottom.value = true;
  emit("image-list-updated", numberPictures.value);
};

const onEnhanceClick = async () => {
  if (numberPictures.value >= 50) {
    const result = await displayCancelConfirmationWarningAlert(`<b>Warning!</b>
    
No more than <b>50 pictures</b> can be added to the AI property listing.

You will only be able to replace this image after enhancing, but not save as new.

Do you want to continue?`);
    if (!result.isConfirmed) return;
  }
  displayAIImageModal.value = true;
};

const imageSavedOrChanged = async (isNewImage: boolean) => {
  if (isNewImage) {
    numberPictures.value++;
    await nextTick();

    imageList.value.push({
      id: numberPictures.value,
      key: generateImageKey(numberPictures.value),
    });

    autoScrollToBottom.value = true;
  } else {
    if (selectedItem.value !== -1) {
      imageList.value[selectedItem.value].key = generateImageKey(
        selectedItemAsImageId.value,
      );
    }
  }

  resetProcessingFields();
  emit("image-list-updated", numberPictures.value);
};

const submitImageToAPI = async (image: File) => {
  try {
    /* As the image data is a byte array and not json, we need to use uploadFile instead of httpPost
     * Otherwise the api will error*/
    await apiClient.uploadFile(
      `app/properties/${props.recordId}/pictures`,
      image,
    );
    return true;
  } catch (err) {
    displayErrorMessage(
      "We could not upload your image, please ensure it is in a .jpg, .jpeg or .gif formats.",
    );
    uploadFailed.value = true;
    return false;
  }
};

const actionMenuIcon = computed(() =>
  showFunctionsMenu.value ? "spindown" : "spinup",
);

const xSmallScreen = computed(() => {
  const screenSizes = sizes();
  if (screenSizes["screen-x-small"]) {
    return true;
  } else return false;
});

const onUploadCancelled = () => {
  triggerFileUploading.value = false;
  resetProcessingFields();
};

// For making the selected image the primary web picture
const makeMainImage = async (e: any, pictureNumber: number) => {
  if (!propertyAllData.value) {
    return;
  }

  // If the checkbox has been set to false and/or then back to true for main picture, there is no point calling the API.
  // In desktop acquaint, if a user deselects the main web image without selecting a new image to be the main web image,
  // nothing occurs - the main web image remains the same.
  if (propertyAllData.value.pictureWebMain == pictureNumber) {
    return;
  }
  propertyAllData.value.pictureWebMain = pictureNumber;

  try {
    await apiClient.httpPutVoid(`database/properties/${props.recordId}`, {
      pictureWebMain: pictureNumber,
    });
  } catch (err) {
    displayErrorMessage("We could not save any changes made.");
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("section", {
      class: _normalizeClass(photoContainerClasses.value)
    }, [
      _createVNode(ScrollView, null, {
        default: _withCtx(() => [
          _withDirectives(_createVNode(_unref(VueDraggableNext), {
            class: "dragArea list-group draggable-section",
            modelValue: imageList.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((imageList).value = $event)),
            onChange: movePicturePlace,
            "item-key": "key",
            disabled: rearrangeDisabled.value,
            handle: "#dragHandle"
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(imageList.value, (image, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  class: "picture-holder",
                  key: image.key
                }, [
                  _hoisted_1,
                  _createVNode(CarouselPicture, {
                    class: _normalizeClass({ selectedItem: selectedItem.value == index && itemSelected.value }),
                    "record-id": props.recordId,
                    pictureNumber: image.id,
                    "record-type": _unref(CarouselPictureType).Property,
                    type: _unref(PropertyImageType).PropertyImageWeb,
                    onClick: ($event: any) => (selectImage(index)),
                    onErrorLoadingPicture: ($event: any) => (onErrorLoadingPicture(index)),
                    onLoaded: ($event: any) => (onImageLoaded(image.id))
                  }, null, 8, ["class", "record-id", "pictureNumber", "record-type", "type", "onClick", "onErrorLoadingPicture", "onLoaded"]),
                  (selectedItem.value == index && itemSelected.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        _createVNode(_unref(DxCheckBox), {
                          text: "Web Main Picture",
                          value: isWebMainPicture(image.id),
                          onValueChanged: (e) => makeMainImage(e, image.id),
                          readOnly: disableButtons.value
                        }, null, 8, ["value", "onValueChanged", "readOnly"])
                      ]))
                    : _createCommentVNode("", true)
                ]))
              }), 128))
            ]),
            _: 1
          }, 8, ["modelValue", "disabled"]), [
            [_vShow, numberPictures.value > 0]
          ])
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_3, [
        (xSmallScreen.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createVNode(_unref(DxButton), {
                elementAttr: { id: contextMenuId },
                text: "Actions",
                onClick: _cache[1] || (_cache[1] = ($event: any) => (showFunctionsMenu.value = !showFunctionsMenu.value)),
                icon: actionMenuIcon.value
              }, null, 8, ["elementAttr", "icon"]),
              _createVNode(_unref(DxContextMenu), {
                visible: showFunctionsMenu.value,
                "onUpdate:visible": _cache[2] || (_cache[2] = ($event: any) => ((showFunctionsMenu).value = $event)),
                "data-source": menuItems.value,
                target: `#${contextMenuId}`,
                position: { my: 'bottom left', at: 'top left' },
                onItemClick: onMenuItemClick
              }, null, 8, ["visible", "data-source", "target"])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(_unref(DxButton), {
                text: "Delete Photo",
                onClick: _cache[3] || (_cache[3] = () => onDeleteClick()),
                disabled: selectedItem.value == -1
              }, null, 8, ["disabled"]),
              _createVNode(_unref(DxButton), {
                text: "Add Photo",
                onClick: _cache[4] || (_cache[4] = () => onAddClick())
              }),
              _createVNode(_unref(DxButton), {
                text: "Zoom",
                onClick: _cache[5] || (_cache[5] = () => onDisplayZoomModal()),
                disabled: selectedItem.value == -1
              }, null, 8, ["disabled"])
            ])),
        _createVNode(_unref(DxButton), {
          text: "Enhance",
          icon: "fa-solid fa-microchip-ai",
          onClick: onEnhanceClick,
          disabled: disableButtons.value || !itemSelected.value || props.pictureCount < 1
        }, null, 8, ["disabled"])
      ]),
      _createElementVNode("div", _hoisted_6, [
        _createVNode(FileUpload, {
          text: "Add Photo",
          onFilesChanged: uploadAddedImage,
          showFileList: false,
          readOnly: false,
          buttonBackground: "#0070d2",
          buttonTextColor: "white",
          triggerFileUploading: triggerFileUploading.value,
          reset: resetFiles.value,
          multiple: true,
          onUploadCancelled: onUploadCancelled
        }, null, 8, ["triggerFileUploading", "reset"])
      ])
    ], 2),
    _createVNode(ImagerViewerModal, {
      modelValue: displayZoomModal.value,
      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((displayZoomModal).value = $event)),
      imageList: imageList.value,
      "record-id": props.recordId.toString(),
      intialImageToDisplay: selectedItemAsImageId.value,
      recordType: _unref(CarouselPictureType).Property
    }, null, 8, ["modelValue", "imageList", "record-id", "intialImageToDisplay", "recordType"]),
    _createVNode(AiImageEnhancerModal, {
      modelValue: displayAIImageModal.value,
      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((displayAIImageModal).value = $event)),
      propertyId: Number(props.recordId),
      imageToDisplay: selectedItemAsImageId.value,
      allowSaveAsNew: numberPictures.value < 50,
      onAiResponseSaved: imageSavedOrChanged
    }, null, 8, ["modelValue", "propertyId", "imageToDisplay", "allowSaveAsNew"])
  ], 64))
}
}

})